import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import schedulesService from '../services/schedules.service';
import { Schedule } from '../types';
import { SchedulesTable, SchedulesToolbar } from '../components/schedules/SchedulesTable';
import { SchedulesForm } from '../components/schedules/SchedulesForm';

export default function SchedulesPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Schedule | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['schedules', { page, limit, search }],
    queryFn: () => schedulesService.getList({ page, limit, search }),
  });

  const createMutation = useMutation({
    mutationFn: (d: any) => schedulesService.create(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['schedules'] }); setIsFormOpen(false); setEditingItem(null); setNotification({ message: 'Расписание добавлено', type: 'success' }); },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => schedulesService.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['schedules'] }); setIsFormOpen(false); setEditingItem(null); setNotification({ message: 'Расписание обновлено', type: 'success' }); },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => schedulesService.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['schedules'] }); setNotification({ message: 'Расписание удалено', type: 'success' }); },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const items = data?.data || [];
  const total = data?.pagination?.total || 0;

  const handleSubmit = async (d: any) => {
    if (editingItem) await updateMutation.mutateAsync({ id: editingItem.id, data: d });
    else await createMutation.mutateAsync(d);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Расписания богослужений</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingItem(null); setIsFormOpen(true); }}>Добавить</Button>
      </Box>
      <SchedulesToolbar search={search} onSearchChange={(v) => { setSearch(v); setPage(1); }} />
      <SchedulesTable items={items} loading={isLoading} total={total} page={page} limit={limit} onPageChange={setPage} onEdit={(i) => { setEditingItem(i); setIsFormOpen(true); }} onDelete={(id) => { if (window.confirm('Удалить расписание?')) deleteMutation.mutate(id); }} />
      <SchedulesForm open={isFormOpen} onClose={() => setIsFormOpen(false)} onSubmit={handleSubmit} initialData={editingItem} loading={createMutation.isPending || updateMutation.isPending} error={(createMutation.error as any)?.message || (updateMutation.error as any)?.message} />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setNotification(null)} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
